import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const TicketsContext = createContext();

export const useTickets = () => useContext(TicketsContext);

export const TicketsProvider = ({ children }) => {
  const navigate = useNavigate();
  
  // Estado para los tickets y departamentos
  const [tickets, setTickets] = useState([]);
  const [departments, setDepartments] = useState([
    { id: 'logistica', name: 'Logística', icon: 'box' },
    { id: 'monitorieo', name: 'Monitorieo', icon: 'activity' },
    { id: 'hseq', name: 'HSEQ', icon: 'users' },
    { id: 'transporte', name: 'Transporte', icon: 'truck' }
  ]);
  
  // Estados para los tickets
  const [sections, setSections] = useState([
    { id: 'reportados', name: 'Reportados', icon: 'alert-triangle' },
    { id: 'en-proceso', name: 'En proceso', icon: 'clock' },
    { id: 'en-prueba', name: 'En prueba', icon: 'flask' },
    { id: 'finalizado', name: 'Finalizado', icon: 'check-circle' },
    { id: 'reabiertos', name: 'Reabiertos', icon: 'refresh-cw' }
  ]);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [currentDepartment, setCurrentDepartment] = useState(null);

  // Datos de ejemplo
  const mockTickets = [
    {
      id: 'TKT-001',
      title: 'Error en sincronización de datos',
      description: 'Los datos no se están sincronizando correctamente entre el frontend y backend.',
      status: 'reportados',
      priority: 'high',
      category: 'technical',
      department: 'logistica',
      created: '2024-01-15T10:30:00Z',
      updated: '2024-01-15T14:22:00Z',
      assignee: 'Juan Pérez',
      reporter: 'María González',
      comments: [
        {
          id: 1,
          author: 'Juan Pérez',
          content: 'Estoy revisando el problema. Parece estar relacionado con la configuración de la base de datos.',
          timestamp: '2024-01-15T11:15:00Z'
        }
      ]
    },
    {
      id: 'TKT-002',
      title: 'Solicitud de nueva funcionalidad',
      description: 'Necesitamos implementar un filtro adicional en el módulo de reportes.',
      status: 'en-proceso',
      priority: 'medium',
      category: 'feature',
      department: 'logistica',
      created: '2024-01-14T09:15:00Z',
      updated: '2024-01-15T16:45:00Z',
      assignee: 'Ana Rodríguez',
      reporter: 'Carlos López',
      comments: []
    },
    {
      id: 'TKT-003',
      title: 'Problema de rendimiento',
      description: 'La aplicación se vuelve lenta cuando hay muchos usuarios conectados.',
      status: 'finalizado',
      priority: 'high',
      category: 'performance',
      department: 'monitorieo',
      created: '2024-01-13T14:20:00Z',
      updated: '2024-01-14T12:30:00Z',
      assignee: 'Luis Martín',
      reporter: 'Sandra Torres',
      comments: [
        {
          id: 1,
          author: 'Luis Martín',
          content: 'Optimicé las consultas de la base de datos. El problema debería estar resuelto.',
          timestamp: '2024-01-14T12:30:00Z'
        }
      ]
    }
  ];

  useEffect(() => {
    setTickets(mockTickets);
  }, []);

  // Obtener tickets por departamento
  const getTicketsByDepartment = (departmentId) => {
    return tickets.filter(ticket => ticket.department === departmentId);
  };

  // Obtener tickets por sección dentro de un departamento
  const getTicketsBySection = (departmentId, sectionId) => {
    return tickets.filter(
      ticket => ticket.department === departmentId && ticket.status === sectionId
    );
  };

  // Conteo de tickets por departamento y sección
  const getTicketCount = (departmentId, sectionId = null) => {
    if (sectionId) {
      return tickets.filter(
        ticket => ticket.department === departmentId && ticket.status === sectionId
      ).length;
    }
    return tickets.filter(ticket => ticket.department === departmentId).length;
  };

  // Crear un nuevo ticket
  const createTicket = (newTicketData) => {
    const ticket = {
      id: `TKT-${String(tickets.length + 1).padStart(3, '0')}`,
      ...newTicketData,
      created: new Date().toISOString(),
      updated: new Date().toISOString(),
      comments: []
    };
    
    setTickets([ticket, ...tickets]);
    setIsCreateModalOpen(false);
  };

  // Mover ticket entre secciones
  const moveTicket = (ticketId, targetStatus) => {
    setTickets(tickets.map(ticket => {
      if (ticket.id === ticketId) {
        return {
          ...ticket,
          status: targetStatus,
          updated: new Date().toISOString()
        };
      }
      return ticket;
    }));
  };

  // Reordenar tickets en la misma sección
  const reorderTickets = (departmentId, sectionId, sourceIndex, destinationIndex) => {
    const sectionTickets = [...getTicketsBySection(departmentId, sectionId)];
    const [removed] = sectionTickets.splice(sourceIndex, 1);
    sectionTickets.splice(destinationIndex, 0, removed);
    
    // Obtener todos los tickets que no están en esta sección
    const otherTickets = tickets.filter(
      ticket => !(ticket.department === departmentId && ticket.status === sectionId)
    );
    
    // Combinar con los tickets reordenados
    setTickets([...otherTickets, ...sectionTickets]);
  };

  // Navegar a la vista de departamento
  const navigateToDepartment = (departmentId) => {
    setCurrentDepartment(departmentId);
    navigate(`/help/tickets/department/${departmentId}`);
  };

  // Abrir modal de detalles
  const openTicketDetail = (ticket) => {
    setSelectedTicket(ticket);
    setIsDetailModalOpen(true);
  };

  const value = {
    tickets,
    departments,
    sections,
    currentDepartment,
    setCurrentDepartment,
    getTicketsByDepartment,
    getTicketsBySection,
    getTicketCount,
    createTicket,
    moveTicket,
    reorderTickets,
    navigateToDepartment,
    isCreateModalOpen,
    setIsCreateModalOpen,
    isDetailModalOpen,
    setIsDetailModalOpen,
    selectedTicket,
    openTicketDetail
  };

  return (
    <TicketsContext.Provider value={value}>
      {children}
    </TicketsContext.Provider>
  );
};